<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Mis Descargas</h1>
      <v-btn color="primary" outlined :disabled="loadingData" @click="fetchRequests">
        <v-icon left small>mdi-refresh</v-icon>
        Actualizar
      </v-btn>
    </v-row>

    <v-card outlined class="mb-6">
      <div class="pa-5 pb-1">
        <div class="d-flex align-center mb-1" style="gap: 10px;">
          <span class="step-badge">1</span>
          <h3>Solicitar exportación</h3>
        </div>
        <p class="text-caption grey--text text--darken-1 mb-3" style="margin-left: 30px;">
          Se genera en segundo plano y queda registrada en el historial de abajo.
        </p>

        <div class="mb-5" style="margin-left: 30px;">
          <v-btn-toggle v-model="exportType" mandatory color="primary" data-test="export-type">
            <v-btn large class="px-6 text-body-1" :value="exportTypes.MEMBERSHIPS">
              <v-icon left>mdi-card-account-details-outline</v-icon>
              Membresías
            </v-btn>
            <v-btn large class="px-6 text-body-1" :value="exportTypes.USERS">
              <v-icon left>mdi-account-multiple-outline</v-icon>
              Usuarios de contrato
            </v-btn>
          </v-btn-toggle>
        </div>

        <v-row class="ma-0" style="margin-left: 30px !important; gap: 32px;">
          <v-col cols="12" md="4" class="pa-0">
            <v-autocomplete
              v-model="exportContrato"
              :items="activeContratos"
              :item-text="contratoOptionLabel"
              item-value="id"
              label="Contrato"
              prepend-inner-icon="mdi-magnify"
              return-object
              clearable
              dense
              hide-details
              :loading="loadingContratos"
            ></v-autocomplete>
            <p class="text-caption grey--text text--darken-1 mt-1 mb-0">
              Déjalo vacío para exportar <strong>todos</strong> los contratos.
            </p>
          </v-col>
          <v-col v-if="isUsersExport" cols="12" md class="pa-0">
            <p class="text-caption grey--text text--darken-1 mb-0">
              Incluye todos los usuarios cargados en el contrato, con su plan, empresa y contrato.
            </p>
          </v-col>
          <v-col v-else cols="12" md class="pa-0">
            <div class="text-caption grey--text text--darken-1 mb-2">Estados a incluir</div>
            <v-chip-group v-model="exportStatusSelected" column multiple>
              <v-chip
                v-for="status in exportStatuses"
                :key="status.value"
                filter
                outlined
                :color="status.color"
                :value="status.value"
              >
                {{ status.name }}
              </v-chip>
            </v-chip-group>
            <p class="text-caption grey--text text--darken-1 mt-1 mb-0">
              {{ exportStatusSelected.length }} de {{ exportStatuses.length }} seleccionados.
            </p>
          </v-col>
        </v-row>
      </div>

      <v-divider></v-divider>
      <div class="pa-4 px-5 d-flex align-center justify-end" style="gap: 16px;">
        <span class="text-caption grey--text text--darken-1">{{ exportSummary }}</span>
        <v-btn color="primary" depressed :loading="requestingExport" @click="requestExport">
          <v-icon left>mdi-file-download-outline</v-icon>
          Solicitar exportación
        </v-btn>
      </div>
    </v-card>

    <div class="d-flex align-center mb-3" style="gap: 10px;">
      <span class="step-badge">2</span>
      <h3>Historial de exportaciones</h3>
    </div>

    <export-requests-table
      :items="requests"
      :loading="loadingData"
      @download="handleDownload"
    ></export-requests-table>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="4000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import ExportRequestsTable from '../components/ExportRequestsTable.vue';
import MembershipsService from '../services/memberships.service';
import ContratoService from '../services/contrato.service';
import { utilsMixin } from '../mixins/utilsMixin';
import { EXPORT_REQUEST_STATUS, EXPORT_TYPES } from '../utils/constants';

// El backend expone exportStatus en 4 valores ('pending'|'processing'|'ready'|
// 'failed'); el spec solo distingue 3 estados visibles, así que 'processing'
// colapsa en el mismo bucket que 'pending' ("Pendiente/Procesando").
const EXPORT_STATUS_TO_UI_STATE = {
  pending: EXPORT_REQUEST_STATUS.PENDING,
  processing: EXPORT_REQUEST_STATUS.PENDING,
  ready: EXPORT_REQUEST_STATUS.READY,
  failed: EXPORT_REQUEST_STATUS.FAILED
};

// Estados de membresía disponibles como filtro de la exportación de "CSV
// Membresías" — el mismo set que antes vivía en la vista de Membresías.
const MEMBERSHIP_STATUSES_FOR_EXPORT = [
  { name: 'Activo', value: 2, color: 'green' },
  { name: 'Desactivado', value: 4, color: 'red' },
  { name: 'Pendiente de pago', value: 5, color: 'primary' },
  { name: 'En revisión', value: 6, color: 'orange' }
];

export default {
  name: 'MyDownloads',
  mixins: [utilsMixin],
  components: {
    ExportRequestsTable
  },
  data() {
    return {
      loadingData: false,
      requests: [],
      contratos: [],
      loadingContratos: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'green',
      exportTypes: EXPORT_TYPES,
      exportType: EXPORT_TYPES.MEMBERSHIPS,
      exportContrato: null,
      exportStatuses: MEMBERSHIP_STATUSES_FOR_EXPORT,
      exportStatusSelected: MEMBERSHIP_STATUSES_FOR_EXPORT.map(s => s.value),
      requestingExport: false
    };
  },
  computed: {
    isUsersExport() {
      return this.exportType === EXPORT_TYPES.USERS;
    },

    activeContratos() {
      return this.contratos
        .filter(c => c.status === 'Activo')
        .sort((a, b) => this.contratoOptionLabel(a).localeCompare(this.contratoOptionLabel(b), 'es'));
    },

    exportSummary() {
      const contratoPart = this.exportContrato
        ? this.contratoOptionLabel(this.exportContrato)
        : 'Todos los contratos';
      if (this.isUsersExport) {
        return `Usuarios · ${contratoPart}`;
      }
      return `${contratoPart} · ${this.exportStatusSelected.length} de ${this.exportStatuses.length} estados`;
    }
  },
  created() {
    this.fetchRequests();
  },
  methods: {
    // Sin filtrar por status: una solicitud vieja puede referenciar un
    // contrato que hoy ya no está "Activo", y aun así hay que mostrar su nombre.
    async fetchContratos() {
      this.loadingContratos = true;
      const result = await ContratoService.getAll();
      this.contratos = result.status === 'ok' ? result.data || [] : [];
      this.loadingContratos = false;
    },

    contratoOptionLabel(c) {
      const empresa = c.company && c.company.name;
      const producto = c.producto && c.producto.nombre;
      return [empresa, c.codigo, producto].filter(Boolean).join(' — ');
    },

    contratoLabel(contratoId) {
      const contrato = this.contratos.find(c => c.id === contratoId);
      return contrato ? this.contratoOptionLabel(contrato) : 'Todos los contratos';
    },

    normalizeRequest(raw) {
      return {
        id: raw.id,
        // Solicitudes previas a la nómina de usuarios no traen exportType.
        exportType: raw.exportType || EXPORT_TYPES.MEMBERSHIPS,
        requestedAt: raw.createdAt,
        filters: {
          contratoId: raw.contratoId,
          contratoName: raw.contratoId ? this.contratoLabel(raw.contratoId) : 'Todos los contratos',
          status: raw.statusFilter ? raw.statusFilter.split(',') : null
        },
        state: EXPORT_STATUS_TO_UI_STATE[raw.exportStatus] || EXPORT_REQUEST_STATUS.PENDING,
        expiresAt: raw.expiresAt
      };
    },

    async fetchRequests() {
      this.loadingData = true;
      if (this.contratos.length === 0) {
        await this.fetchContratos();
      }
      const result = await MembershipsService.getMyExportRequests();
      if (result.status === 'ok') {
        this.requests = (result.data || []).map(this.normalizeRequest);
      } else {
        this.showErrorSnackbar('Error al cargar tus descargas');
      }
      this.loadingData = false;
    },

    // Contrato vacío = exportar todos los contratos; es la misma solicitud,
    // no una acción separada ("Exportar todas" ya no existe como botón aparte).
    // Membresías y usuarios van a la misma cola y al mismo historial.
    async requestExport() {
      this.requestingExport = true;
      const contratoId = this.exportContrato && this.exportContrato.id ? this.exportContrato.id : null;
      const result = this.isUsersExport
        ? await MembershipsService.requestUsersExport(contratoId)
        : await MembershipsService.requestMembershipsExport(contratoId, this.selectedStatusCsv(), 'CL');
      if (result.status === 'ok') {
        this.showSuccessSnackbar('Tu exportación fue solicitada');
        this.fetchRequests();
      } else {
        this.showErrorSnackbar('Ocurrió un error al solicitar la exportación');
      }
      this.requestingExport = false;
    },

    selectedStatusCsv() {
      return this.exportStatusSelected.length > 0 ? this.exportStatusSelected.join(',') : null;
    },

    async handleDownload(requestId) {
      const result = await MembershipsService.downloadExportRequestFile(requestId);
      if (result.status === 'ok') {
        return;
      }
      if (result.code === 'expired') {
        this.showErrorSnackbar('El archivo ya no está disponible. Genera una nueva exportación arriba.');
      } else {
        this.showErrorSnackbar('Ocurrió un error al descargar el archivo');
      }
    }
  }
};
</script>

<style scoped>
.step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--v-primary-base);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  flex: none;
}
</style>
